import { COUNTRIES, Country } from "../data/countries";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";

export interface CountrySelectorProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Ülke seçici — arama + bayrak.
 *
 * Liste `document.body`'ye portal edilir. Persona setup kartı
 * (`QuestCard`) köşe/gradient kesmek için `overflow-hidden` kullanıyor;
 * liste kartın içinde `absolute` kalırsa alt kenardan kırpılıyor.
 */
export const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [box, setBox] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const selectedCountry =
    COUNTRIES.find((country) => country.value === value) || null;

  const placeMenu = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 8;
    const width = rect.width;
    const left = Math.min(
      rect.left,
      Math.max(8, window.innerWidth - width - 8)
    );
    const below = window.innerHeight - rect.bottom - gap - 8;
    const above = rect.top - gap - 8;
    const openUp = below < 240 && above > below;
    const maxHeight = Math.min(320, Math.max(openUp ? above : below, 160));
    setBox({
      top: openUp ? rect.top - gap - maxHeight : rect.bottom + gap,
      left,
      width,
      maxHeight,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
      setQuery("");
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) placeMenu();
    setIsOpen((open) => !open);
  };

  const handleSelect = (country: Country) => {
    onChange(country.value);
    setIsOpen(false);
    setQuery("");
  };

  const filteredCountries = COUNTRIES.filter((country) =>
    country.title.toLowerCase().startsWith(query.toLowerCase())
  );

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        className={`
          relative w-full px-3.5 py-2.5 rounded-lg 
          border border-border-primary dark:border-gray-600 
          h-[2.75rem] min-h-[2.75rem]
          ${
            disabled
              ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
              : "bg-white dark:bg-gray-900 cursor-pointer hover:border-text-brand dark:hover:border-indigo-400"
          }
          text-left text-base leading-6
          focus:outline-none focus:ring-2 focus:ring-text-brand dark:focus:ring-indigo-400 focus:border-transparent
          transition-all duration-200
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className="flex items-center truncate">
          {selectedCountry ? (
            <>
              <img
                alt={selectedCountry.value}
                src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry.value}.svg`}
                className="inline mr-2 h-4 w-6 rounded-sm object-cover"
              />
              <span className="text-text-primary dark:text-gray-100">
                {selectedCountry.title}
              </span>
            </>
          ) : (
            <span className="text-text-tertiary dark:text-gray-400">
              {placeholder || t("settings.profile.countryPlaceholder")}
            </span>
          )}
        </span>
        {!disabled && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ChevronDown
              className={`h-5 w-5 text-text-tertiary dark:text-gray-400 transition-transform duration-200 ${
                isOpen ? "transform rotate-180" : ""
              }`}
            />
          </span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && box && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              style={{
                top: box.top,
                left: box.left,
                width: box.width,
                maxHeight: box.maxHeight,
              }}
              className="fixed z-[80] flex flex-col overflow-hidden rounded-lg bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-600"
              role="listbox"
            >
              <div className="sticky top-0 z-10 shrink-0 border-b border-border-primary bg-white dark:border-gray-600 dark:bg-gray-800">
                <div className="p-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary dark:text-gray-400" />
                    <input
                      type="search"
                      name="search"
                      autoComplete="off"
                      autoFocus
                      className="w-full rounded-md border border-border-primary bg-white py-2 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-placeholder focus:border-transparent focus:outline-none focus:ring-2 focus:ring-text-brand dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-indigo-400"
                      placeholder={t("settings.profile.searchCountry")}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                {filteredCountries.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-text-tertiary dark:text-gray-400">
                    {t("settings.profile.noCountriesFound")}
                  </div>
                ) : (
                  filteredCountries.map((country) => (
                    <button
                      key={country.value}
                      type="button"
                      className={`
                      w-full text-left px-3 py-2.5 flex items-center
                      hover:bg-gray-50 dark:hover:bg-gray-700
                      transition-colors duration-150
                      ${
                        country.value === value
                          ? "bg-blue-50 dark:bg-indigo-500/15"
                          : ""
                      }
                    `}
                      role="option"
                      aria-selected={country.value === value}
                      onClick={() => handleSelect(country)}
                    >
                      <img
                        alt={country.value}
                        src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${country.value}.svg`}
                        className="mr-3 inline h-4 w-6 rounded-sm object-cover"
                      />
                      <span className="truncate font-normal text-text-primary dark:text-gray-100">
                        {country.title}
                      </span>
                      {country.value === value && (
                        <span className="ml-auto text-text-brand dark:text-indigo-400">
                          <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};
